import {
    ProcessField as _ProcessField,
    JsonString,
    ProcessFieldType,
} from '@shared/types/process';

import { isDeepEqual } from './isDeepEqual';

import dayjs from 'dayjs';

export function stringifyFieldDefaultValue(field: _ProcessField) {
    if (
        field.typeId === 'Photo' &&
        (field.defaultValue == null || field.defaultValue === '')
    ) {
        return JSON.stringify([]);
    }

    return stringifyJsonValue(field.defaultValue, field.typeId);
}

export function parseFieldDefaultValue(field: _ProcessField) {
    return parseJsonValue(field.defaultValue, field.typeId);
}

export function stringifyJsonValue(
    value: unknown,
    type: ProcessFieldType | null = null,
): JsonString | undefined {
    try {
        let raw = value;

        if (type === 'DateRange') {
            try {
                raw = [
                    dayjs((value as [string, string])[0]).toISOString(),
                    dayjs((value as [string, string])[1]).toISOString(),
                ];
            } catch {
                return undefined;
            }
        }

        if (type === 'Date') {
            try {
                raw = dayjs(value as string).toISOString();
            } catch {
                return undefined;
            }
        }

        return JSON.stringify(raw);
    } catch {
        return undefined;
    }
}

export function parseJsonValue(
    value: unknown,
    type: ProcessFieldType | null = null,
) {
    try {
        let parsed = JSON.parse(value as string);

        if (type === 'DateRange') {
            if (Array.isArray(parsed)) {
                parsed = [dayjs(parsed[0]), dayjs(parsed[1])];
            }
        }
        if (typeof parsed === 'number') {
            return parsed.toString();
        }
        return parsed;
    } catch {
        return undefined;
    }
}

export function parseJsonValueOrRaw(value: unknown) {
    const parsed = parseJsonValue(value);

    return parsed === undefined ? value : parsed;
}

export function isEqualJsonValues(a: unknown, b: unknown): boolean {
    const firstArgument = normalizeJsonComparableValue(a);
    const secondArgument = normalizeJsonComparableValue(b);

    if (Array.isArray(firstArgument) && Array.isArray(secondArgument)) {
        if (firstArgument.length !== secondArgument.length) {
            return false;
        }

        const unmatchedItems = [...secondArgument];

        return firstArgument.every((item) => {
            const matchedIndex = unmatchedItems.findIndex((secondItem) =>
                isDeepEqual(item, secondItem, false),
            );

            if (matchedIndex === -1) {
                return false;
            }

            unmatchedItems.splice(matchedIndex, 1);
            return true;
        });
    }

    return isDeepEqual(firstArgument, secondArgument, false);
}

function normalizeJsonComparableValue(value: unknown) {
    if (typeof value === 'string') {
        return parseJsonValueOrRaw(value);
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    return value;
}
